import React, { useCallback, useState, useRef } from 'react'
import {
  FormattedDate,
  FormattedTime,
  FormattedMessage,
  useIntl,
} from 'react-intl'
import FileIcon from 'components/FileIcon'
import Icon from 'components/Icon'
import messages from './messages'
import classNames from 'classnames'
import { Button } from '@arnsbogroup/react-voicearchive'

const Delivery = ({ timestamp, body, title, last, meta, onRefetch }) => {
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const inputRef = useRef(null)

  const { formatDate, formatTime,formatMessage } = useIntl()
  const { files = [], url } = meta
  const dateTime = new Date(timestamp * 1000);

  const groupedFiles = files.reduce((acc, file) => {
    const { supplierUserIds } = file;
    if (!acc[supplierUserIds]) {
      acc[supplierUserIds] = [];
    }
    acc[supplierUserIds].push(file);
    return acc;
  }, {});

  const onSubmit = useCallback((approved = false) => {
    setLoading(true)

    fetch('/rest/v1/task/feedback', {
      method: 'POST',
      body: JSON.stringify({
        approved,
        feedback,
        fileIds: files.map(({ id }) => id),
      })
    })
      .then(response => response.json())
      .then(({ success, data, errors }) => {
        if (!success) {
          window.alert(errors.join(', '))
        }
        onRefetch()
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setLoading(false)
        setFeedback(false)
      })
  }, [feedback, files]);

  return (
    <li>
      <div className="relative pb-8">
        { !last &&
          <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
        }
        <div className="relative flex items-start space-x-3">
          <div>
            <div className="relative px-2 mt-2">
              <div className="h-6 w-6 bg-positive rounded-full ring-4 ring-white flex justify-center items-center">
                <Icon icon="check" className="text-xs text-white" />
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1 py-0">
            <div className="text-sm leading-6">
              <div className="border border-gray-200 rounded-sm mb-2">
                <div className="p-2 flex justify-between items-center">
                  <span className='my-auto'>{ title }</span>
                  <Button
                    size="sm"
                    onClick={() => window.open(url, '_blank')}
                  >
                    <FormattedMessage {...messages.downloadAll} /> <Icon icon="file-archive" />
                  </Button>
                </div>

                {Object.entries(groupedFiles).map(([supplierUserIds, supplierFiles]) => (
                  <div key={supplierUserIds} className="pb-2">
                    <div className="p-2 text-sm font-bold font-mono break-all border-t border-gray-200">{supplierUserIds}</div>
                    <div className="space-y-4">
                      {supplierFiles.map(({ id, name, url, timestamp, revised, review }) => {
                        const dateTime = new Date(timestamp * 1000);
                        return (
                          <div
                            key={id}
                            className={classNames('p-2 space-y-2', {
                              'bg-yellow-50': revised,
                              'bg-red-50': review.status === 'DECLINED',
                            })}
                          >
                            <div
                              className={classNames('text-xs font-medium font-mono break-all', {
                                'line-through': review.status === 'DECLINED',
                              })}
                              title={name}
                            >
                              {name}
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <div className="flex space-x-1 text-gray-500">
                                <FileIcon filename={name} />
                                <div>
                                  <FormattedDate value={dateTime} />
                                </div>
                                <div>
                                  <FormattedTime value={dateTime} />
                                </div>
                                {revised && (
                                  <div className="font-medium">
                                    &middot; <FormattedMessage {...messages.revised} />
                                  </div>
                                )}
                              </div>
                              <span className="space-x-2">
                                <a
                                  target="_blank"
                                  href={url}
                                  className="shrink-0 text-primary cursor-pointer hover:text-primary-darker space-x-1"
                                >
                                  <FormattedMessage {...messages.download} tagName="span" />
                                  <Icon icon="arrow-down" />
                                </a>
                                {review.status === 'APPROVED' ? (
                                  <Icon icon="check" className="text-positive" title="Approved" />
                                ) : review.status === 'PENDING' ? (
                                  <Icon icon="circle" className="text-gray-400" title="Pending" />
                                ) : (
                                  <Icon icon="times" className="text-negative" title={review.comment} />
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                { files.some(({ review: { status } }) => status == 'PENDING') &&
                    <form>
                      <div className="bg-gray-50 flex items-center justify-between border-t border-gray-200 p-2">
                        { loading ? (
                          <span className="px-1 text-gray-400">
                            <FormattedMessage {...messages.loading } />..
                          </span>
                        ) : feedback ? (
                          <div className='w-full text-right'>
                            <Button
                              size="sm"
                              disabled={loading || !feedback.length}
                              onClick={() => onSubmit(false)}
                            >
                              <FormattedMessage className="font-bold" {...messages.submit} />
                            </Button>
                          </div>
                        ) : (
                          <React.Fragment>
                            <Button
                              size="xs"
                              state="positive"
                              variant="inline"
                              disabled={loading}
                              onClick={() => onSubmit(true)}
                              icon={['fa', 'thumbs-up']}
                              iconPosition='right'
                            >
                              <FormattedMessage {...messages.accept}/>
                            </Button>

                            <Button
                              size="xs"
                              state="negative"
                              variant="inline"
                              disabled={loading}
                              onClick={() => setFeedback('') && inputRef.current.focus()}
                              icon={['fa', 'thumbs-down']}
                              iconPosition='right'
                            >
                              <FormattedMessage {...messages.decline}/>
                            </Button>
                          </React.Fragment>
                        ) }
                      </div>

                      { feedback !== false &&
                        <textarea
                          ref={inputRef}
                          className="px-4 py-2 rounded-sm text-sm w-full m-0 border-t border-gray-200"
                          rows={1}
                          placeholder={formatMessage(messages.feedbackMessage)}
                          value={feedback}
                          onChange={({ target: { value } }) => setFeedback(value)}
                          onFocus={() => inputRef.current.classList.add('h-20')}
                          onBlur={() => {
                            if (inputRef.current && !feedback.length) {
                              inputRef.current.classList.remove('h-20')
                            }
                          }}
                        />
                      }
                    </form>
                }
              </div>
              <span className="whitespace-nowrap text-gray-500 text-xs" title={`${formatDate(dateTime)} ${formatTime(dateTime)}`}>
                <FormattedDate value={timestamp * 1000} /> <FormattedTime value={timestamp * 1000} />
              </span>
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}
export default Delivery
